import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  staffLoginApi,
  createStaffApi,
  getStaffApi,
  getStaffByIdApi,
  updateStaffApi,
  updateStaffPermissionsApi,
  toggleStaffStatusApi,
  deleteStaffApi
} from "../../api/staffApi";

// Initial state
const initialState = {
  staff: [],
  currentStaff: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  },
  filters: {
    role: "",
    isActive: "",
    search: ""
  }
};

// ══════════════════════════════════════════════════════════════════
// Async Thunks
// ══════════════════════════════════════════════════════════════════

export const staffLogin = createAsyncThunk(
  "staff/login",
  async (form, { rejectWithValue }) => {
    try {
      const data = await staffLoginApi(form);
      // Store staff token separately
      localStorage.setItem("restaurantApp_token", data.accessToken);
      localStorage.setItem("restaurantApp_refreshToken", data.refreshToken);
      localStorage.setItem("restaurantApp_user", JSON.stringify(data.staff));

      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Login failed"
      );
    }
  }
);

// Create staff
export const createStaff = createAsyncThunk(
  "staff/create",
  async (staffData, { rejectWithValue }) => {
    try {
      const response = await createStaffApi(staffData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get all staff
export const getStaff = createAsyncThunk(
  "staff/getAll",
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const { staff } = getState();
      const queryParams = {
        page: params.page || staff.pagination.page,
        limit: params.limit || staff.pagination.limit,
        role: params.role !== undefined ? params.role : staff.filters.role,
        isActive: params.isActive !== undefined ? params.isActive : staff.filters.isActive,
        search: params.search !== undefined ? params.search : staff.filters.search
      };
      const response = await getStaffApi(queryParams);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get staff by ID
export const getStaffById = createAsyncThunk(
  "staff/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getStaffByIdApi(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Update staff
export const updateStaff = createAsyncThunk(
  "staff/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await updateStaffApi( id, formData );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Update staff permissions
export const updateStaffPermissions = createAsyncThunk(
  "staff/updatePermissions",
  async ({ id, permissions }, { rejectWithValue }) => {
    try {
      const response = await updateStaffPermissionsApi({ id, permissions });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Toggle staff status
export const toggleStaffStatus = createAsyncThunk(
  "staff/toggleStatus",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await toggleStaffStatusApi(id);
      // Refresh staff list after toggling status
      dispatch(getStaff());
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete staff
export const deleteStaff = createAsyncThunk(
  "staff/delete",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await deleteStaffApi(id);
      // Refresh staff list after deletion
      dispatch(getStaff());
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ══════════════════════════════════════════════════════════════════
// Slice
// ══════════════════════════════════════════════════════════════════

const staffSlice = createSlice({
  name: "staff",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearCurrentStaff: (state) => {
      state.currentStaff = null;
    },
    staffLogout: (state) => {
      state.currentStaff = null;
      state.isStaffAuthenticated = false;
      localStorage.removeItem("restaurantApp_token");
      localStorage.removeItem("restaurantApp_refreshToken");
      localStorage.removeItem("restaurantApp_user");
    },
  },
  extraReducers: (builder) => {
    builder
    // ── Staff Login ──────────────────────────────────────────────
      .addCase(staffLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(staffLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.isStaffAuthenticated = true;
        state.currentStaff = action.payload.staff;
        state.success = action.payload.message;
      })
      .addCase(staffLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create staff
      .addCase(createStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStaff.fulfilled, (state, action) => {
        state.loading = false;
        state.staff.unshift(action.payload.data);
      })
      .addCase(createStaff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get all staff
      .addCase(getStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getStaff.fulfilled, (state, action) => {
        state.loading = false;
        state.staff = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(getStaff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get staff by ID
      .addCase(getStaffById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getStaffById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentStaff = action.payload.data;
      })
      .addCase(getStaffById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update staff
      .addCase(updateStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStaff.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.staff.findIndex(s => s._id === action.payload.data._id);
        if (index !== -1) {
          state.staff[index] = action.payload.data;
        }
        if (state.currentStaff?._id === action.payload.data._id) {
          state.currentStaff = action.payload.data;
        }
      })
      .addCase(updateStaff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update permissions
      .addCase(updateStaffPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStaffPermissions.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.staff.findIndex(s => s._id === action.payload.data.id);
        if (index !== -1) {
          state.staff[index].permissions = action.payload.data.permissions;
        }
        if (state.currentStaff?._id === action.payload.data.id) {
          state.currentStaff.permissions = action.payload.data.permissions;
        }
      })
      .addCase(updateStaffPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Toggle status
      .addCase(toggleStaffStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleStaffStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.staff.findIndex(s => s._id === action.payload.data.id);
        if (index !== -1) {
          state.staff[index].isActive = action.payload.data.isActive;
        }
        if (state.currentStaff?._id === action.payload.data.id) {
          state.currentStaff.isActive = action.payload.data.isActive;
        }
      })
      .addCase(toggleStaffStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete staff
      .addCase(deleteStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStaff.fulfilled, (state, action) => {
        state.loading = false;
        state.staff = state.staff.filter(s => s._id !== action.meta.arg);
        if (state.currentStaff?._id === action.meta.arg) {
          state.currentStaff = null;
        }
      })
      .addCase(deleteStaff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, setFilters, resetFilters, clearCurrentStaff } = staffSlice.actions;
export default staffSlice.reducer;